import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Store, select } from '@ngrx/store';
import {
	ClearingUniversityProfile,
	ClientFeatureOptions,
	ClientFeatures,
	FeaturedCalls,
	FeaturedClearing,
	FeaturedClearingVisitWebsite,
	FeaturedPostgraduateLandingPage,
	FeaturedRegisteredInterest,
	Gallery,
} from '@uc/web/shared/data-models';
import { getStateClearingUniversity } from '../../data-access/+state/university.selectors';
import { Subscription, filter, map, take } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { ClientValidatorService } from '@uc/web/core';
import { fetchClearingUniversity } from '../../data-access/+state/university.actions';
import * as SEO from '../../utils/seo-metadata.utils';
import {
	CtaClearingComponent,
	CtaClearingNonFeaturedComponent,
	UniversitySliderComponent,
	UniversitySliderCopy,
} from '@uc/web/shared/features/feature-university-slider';
import {
	ClearingService,
	RemoveDuplicateDomElementDirective,
	SeoMetadataService,
	isStoreEmpty,
} from '@uc/utilities';
import {
	CLEARING_COPY,
	getNonClientClearingCopy,
	preClearingCopy,
	POST_CLEARING_COPY,
} from '../../static/clearing-profile.properties';
import { VideoGAService, UniversityGAService, NA } from '@uc/web/analytics';
import { UniversitySkeletonComponent } from '../../components/university-skeleton/university-skeleton.component';
import { NavigationTabsComponent } from '../../components/navigation-tabs/navigation-tabs.component';
import { CourseSearchBannerComponent, UniversityHeaderComponent } from '@web/shared/ui';
import {
	ClearingAvailableBadgeComponent,
	ClearingCtaFinalComponent,
} from '@uc/web/shared/clearing-ui';
import { UniversityHeaderTitleComponent } from '../../components/university-header-title/university-header-title.component';
import { UniversityInformationComponent } from '../../components/university-information/university-information.component';
import { HeadingComponent } from '@uc/shared/ui';
import { RecommendedUnisGridComponent } from '@uc/web/shared/features/feature-recommended-universities';
import {
	ClientProfileStickyLeadBarDesktopComponent,
	ClientProfileStickyLeadBarMobileComponent,
} from '@web/shared/features/feature-sticky-lead-bar';
import { Product, ProductEnum } from '@uc/shared/data-models';
import { ClearingVisitWebsiteBtnComponent } from '@uc/web/shared/leads';

@Component({
	selector: 'uc-clearing-profile',
	templateUrl: './clearing-profile.component.html',
	imports: [
		AsyncPipe,
		RemoveDuplicateDomElementDirective,
		UniversitySkeletonComponent,
		NavigationTabsComponent,
		UniversityHeaderComponent,
		UniversityHeaderTitleComponent,
		ClearingAvailableBadgeComponent,
		UniversityInformationComponent,
		HeadingComponent,
		ClearingVisitWebsiteBtnComponent,
		CourseSearchBannerComponent,
		ClearingCtaFinalComponent,
		ClientProfileStickyLeadBarMobileComponent,
		ClientProfileStickyLeadBarDesktopComponent,
		RecommendedUnisGridComponent,
		CtaClearingComponent,
		CtaClearingNonFeaturedComponent,
		UniversitySliderComponent,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ClearingProfileComponent implements OnInit, OnDestroy {
	readonly paidFeatures = [ClientFeatures.Clearing];
	product: Product = ProductEnum.Clearing;
	isLoading = false;
	uniSliderCopy = {} as UniversitySliderCopy;
	uniGallery!: Gallery;
	isFeaturedClearing!: FeaturedClearing;
	isFeaturedRegisterInterest!: FeaturedRegisteredInterest;
	isFeaturedClearingVisitWebsite!: FeaturedClearingVisitWebsite;
	isFeaturedCalls!: FeaturedCalls;
	isFeaturedPostgraduateLandingPage!: FeaturedPostgraduateLandingPage;
	isPreview = this._route.snapshot.queryParams['preview'] === 'true';
	uniSlug = this._route.parent?.snapshot.params['uniName'];
	currentClearingYear = this._clearingSrv.getYear();
	isClearing = this._clearingSrv.isClearing();
	isPreClearing = this._clearingSrv.isPreClearing();
	isPostClearing = this._clearingSrv.isPostClearing();

	private _currentYear = new Date().getFullYear();

	university$ = this._store.pipe(
		select(getStateClearingUniversity),
		filter((university) => !isStoreEmpty(university)),
		map((university) => {
			if (!university) return;

			this.isLoading = false;

			this.hasFeatures(university.paid_features);
			this.getClearingCopy(university.name);
			this.handleSEO(university.name);

			this.uniGallery = {
				cover_image: university.cover_image,
				images: university.gallery,
				video: university.video,
			};

			return university;
		}),
	);

	private _routeSub!: Subscription | undefined;

	constructor(
		private _store: Store,
		private _route: ActivatedRoute,
		private _seoSrv: SeoMetadataService,
		private _cvSrv: ClientValidatorService,
		private _clearingSrv: ClearingService,
		private _videoGASrv: VideoGAService,
		private _uniGASrv: UniversityGAService,
	) {}

	ngOnInit(): void {
		this.isLoading = true;
		// Display the preview view only if the preview query param is present in the url
		if (this.isPreview) {
			this.paidFeatures.push(ClientFeatures.ClearingPreview);
		}

		this._routeSub = this._route.paramMap.subscribe((params) => {
			this.uniSlug = params.get('uniName');
			this._store.dispatch(
				fetchClearingUniversity({
					uniSlug: this.uniSlug,
				}),
			);
		});

		this.onUniProfileImpression();
	}

	ngOnDestroy(): void {
		this._routeSub?.unsubscribe();
	}

	handleSEO(universityName: string): void {
		const title = SEO.generateTitle(universityName, 'clearing');
		const description = SEO.generateDescription(universityName, 'clearing');
		this._seoSrv.updateTitle(title);
		this._seoSrv.updateDescription(description);
	}

	getClearingCopy(uniName: string) {
		if (this.isClearing || this.isPreview) {
			this.uniSliderCopy = this.isFeaturedClearing
				? CLEARING_COPY
				: getNonClientClearingCopy(this.currentClearingYear);
		} else if (this.isPreClearing) {
			this.uniSliderCopy = preClearingCopy(this._currentYear, uniName);
		} else if (this.isPostClearing) {
			this.uniSliderCopy = POST_CLEARING_COPY;
		}
	}

	hasFeatures(paidFeatures: ClientFeatureOptions[]) {
		this.isFeaturedClearing = this._cvSrv.checkFeature(
			paidFeatures,
			this.paidFeatures,
		);

		this.isFeaturedCalls = this._cvSrv.checkFeature(
			paidFeatures,
			ClientFeatures.Calls,
		);

		this.isFeaturedRegisterInterest = this._cvSrv.checkFeature(
			paidFeatures,
			ClientFeatures.RegisterInterest,
		);

		this.isFeaturedClearingVisitWebsite = this._cvSrv.checkFeature(
			paidFeatures,
			ClientFeatures.ClearingVisitWebsite,
		);

		this.isFeaturedPostgraduateLandingPage = this._cvSrv.checkFeature(
			paidFeatures,
			ClientFeatures.PostgraduateLandingPage,
		);
	}

	onUniProfileImpression() {
		this.university$
			.pipe(
				filter((uni) => !!uni?.id),
				take(1),
				map((uni) => this._trackUniEvents(uni)),
			)
			.subscribe();
	}

	private _trackUniEvents(uni: ClearingUniversityProfile | undefined) {
		if (!uni) return;

		const { id, name, order, address, video } = uni;
		const degree = 'Clearing';
		this._uniGASrv.uniImpression(
			id,
			name,
			null,
			order,
			this.product,
			null,
			NA,
			degree,
			address.region ?? NA,
			degree,
		);

		this._uniGASrv.uniProfileView(id, name, null, order, this.product);

		if (video?.url) {
			this._videoGASrv.impression(uni.id, ProductEnum.Clearing);
		}
	}

	// shouldDisplayFaq(faqs: FAQ[]) {
	// 	return this._universityUtils.shouldDisplayFaq(this.isPreview, faqs);
	// }
}
